import React, { useMemo, useState } from 'react';
import styles from './SelectBox.module.css';
import Skeleton from '../../Skeleton/Skeleton';
import Image from '../../Image/Image';
import { Popover } from '@round/ui-kit';
import OptionsList, { ListItemProps } from './components/OptionsList/OptionsList';
import { SelectBoxOption } from './SelectBox.types.';
import Option from './components/Option/Option';

type SelectBoxProps<T extends SelectBoxOption> = {
    loading: boolean;
    value: T | undefined;
    onChange: (value: T) => void;
    options: T[];
    optionsCount: number;
    optionsLoading: boolean;
    loadMore?: () => Promise<void>;
    search?: string;
    setSearch?: (search: string) => void;
    searchable?: boolean;
    noOptionsLabel?: string;
    placeholder?: string;
    renderDropdownBottom?: (setShowDropdown: (show: boolean) => void) => React.ReactNode;
    renderItem?: (props: RenderItemProps<T>) => React.ReactNode;
};

type RenderItemProps<T extends SelectBoxOption> = ListItemProps<T> & {
    onClick: () => void;
    setShowDropdown: (show: boolean) => void;
};

const SelectBox = <T extends SelectBoxOption>({
    optionsLoading,
    optionsCount,
    loading,
    options,
    onChange,
    value,
    loadMore,
    search,
    setSearch,
    searchable,
    noOptionsLabel = 'No Options',
    placeholder,
    renderDropdownBottom,
    renderItem,
}: SelectBoxProps<T>) => {
    const [valueContainerRef, setValueContainerRef] = useState<HTMLDivElement | null>(null);
    const valueContainerWidth = useMemo(() => valueContainerRef?.getBoundingClientRect().width, [valueContainerRef]);
    const name = value?.name ?? placeholder;

    return (
        <>
            <div ref={setValueContainerRef} className={styles.valueContainer}>
                {loading ? (
                    <Skeleton className={styles.image} enableAnimation={false} />
                ) : (
                    <Image className={styles.image} src={value?.picture} />
                )}

                <div className={styles.values}>
                    <p className={styles.name}>{loading ? <Skeleton width="75%" enableAnimation={false} /> : name}</p>
                    <p className={styles.subtitle}>
                        {loading ? <Skeleton width="50%" enableAnimation={false} /> : value?.subtitle}
                    </p>
                </div>
            </div>

            <Popover anchorElement={valueContainerRef} style={{ width: valueContainerWidth }} showOn="click">
                {(setShow) => (
                    <div className={styles.dropdown} data-test-id="select-box-dropdown">
                        <OptionsList
                            loading={optionsLoading}
                            searchable={searchable}
                            options={options}
                            count={optionsCount}
                            searchQuery={search}
                            setSearchQuery={setSearch}
                            noOptionsLabel={noOptionsLabel}
                            loadMore={loadMore}
                        >
                            {({ item, style, loading }) => {
                                const handleClick = () => {
                                    if (!item) {
                                        return;
                                    }

                                    onChange(item);
                                    setShow(false);
                                };

                                if (typeof renderItem === 'function') {
                                    return renderItem({
                                        item,
                                        style,
                                        loading,
                                        onClick: handleClick,
                                        setShowDropdown: setShow,
                                    });
                                }

                                return (
                                    <Option
                                        item={item}
                                        style={style}
                                        loading={loading}
                                        isActive={item === value}
                                        onClick={(item, e) => {
                                            e.stopPropagation();
                                            handleClick();
                                        }}
                                    />
                                );
                            }}
                        </OptionsList>
                        {typeof renderDropdownBottom === 'function' && renderDropdownBottom(setShow)}
                    </div>
                )}
            </Popover>
        </>
    );
};

export default SelectBox;
